import { memo, useMemo } from 'react';
import { Version } from 'models';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@material-ui/core';

import { HTMLParser, PageTitle } from 'containers';
import {
  FiltersBar,
  PageLoader,
  Error,
  Download,
  Select,
  DatePicker,
  ControlWrapper,
} from 'components';

import { useStyles } from './style';

interface Props {
  fileName: string;
  startDate: Date | null;
  endDate: Date | null;
  version: Version;
  url: string;
  loading: boolean;
  error: boolean;
  handleChangeDate: (date: Date | null, name?: string) => void;
  handleChangeVersion: (version: Version) => void;
}

const versions: Version[] = [
  { label: '#portal.investor.report.net-income.basedon.fee-based', value: 0 },
  {
    label: '#portal.investor.report.net-income.basedon.calculationsbased',
    value: 1,
  },
];

const NetIncomeDetails: React.FC<Props> = ({
  startDate,
  endDate,
  fileName,
  version,
  url,
  loading,
  error,
  handleChangeDate,
  handleChangeVersion,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(theme.size.sm));

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
    }),
    [classes]
  );

  if (error) return <Error />;
  if (loading) return <PageLoader />;

  return (
    <>
      <PageTitle />
      <FiltersBar className={classes.filtersBar}>
        <ControlWrapper className={classes.controlWrapper}>
          <span className={classes.label}>
            {t('#portal.investor.report.net-income.basedon')}:
          </span>
          <Select
            value={version}
            options={versions}
            cls={selectCls}
            onSelect={handleChangeVersion}
            labelField="label"
            valueField="value"
            withButton={true}
            withTranslation={true}
          />
        </ControlWrapper>
        <div className={classes.datePickersWrapper}>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {t('#portal.investor.report.net-income.startdate')}:
            </span>
            <DatePicker
              name="startDate"
              date={startDate}
              handleChange={handleChangeDate}
              maxDate={endDate}
            />
          </ControlWrapper>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {t('#portal.investor.report.net-income.enddate')}:
            </span>
            <DatePicker
              name="endDate"
              date={endDate}
              handleChange={handleChangeDate}
              minDate={startDate}
            />
          </ControlWrapper>
        </div>
        <div className={classes.btnsWrapper}>
          <ControlWrapper>
            <Download name={fileName} url={`${url}&download=true`} />
          </ControlWrapper>
          {!mobileView && (
            <ControlWrapper>
              <Download
                name={fileName}
                url={`${url}&download=true&downloadFormat=7`}
                extension="xls"
              />
            </ControlWrapper>
          )}
        </div>
      </FiltersBar>
      {url ? <HTMLParser url={url} /> : null}
    </>
  );
};

export default memo(NetIncomeDetails);
